import { useQuickStats } from './api/useAdvertisersData';
import {
  QuickStatsQuery,
  QuickStatsResponse,
} from '@/types/advertisers.types';

interface UseDashboardStatsOptions {
  immediate?: boolean;
  params?: QuickStatsQuery;
}

interface UseDashboardStatsReturn {
  data: QuickStatsResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Custom hook for fetching dashboard quick stats
 * Now powered by React Query for automatic caching and deduplication
 * Provides type-safe access to advertiser performance metrics
 *
 * @param options Configuration options
 * @returns Dashboard stats data, loading state, error, and refetch function
 *
 * @example
 * const { data, loading, error, refetch } = useDashboardStats({
 *   params: { fromDate: '2024-12-01', toDate: '2024-12-17' }
 * });
 */
export const useDashboardStats = (
  options: UseDashboardStatsOptions = {}
): UseDashboardStatsReturn => {
  const { params } = options;

  // Use React Query hook with automatic caching and deduplication
  const {
    data: queryData,
    isLoading,
    error: queryError,
    refetch,
  } = useQuickStats(params, {
    enabled: Boolean(params?.fromDate && params?.toDate),
  });

  return {
    data: queryData?.data || null,
    loading: isLoading,
    error: queryError ? (queryError as any).message || 'Failed to fetch dashboard stats' : null,
    refetch,
  };
};

export default useDashboardStats;
